import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const RELAY_URL = process.env.REACT_APP_RELAY_URL || 'http://localhost:3001';

// С момента введения admin_sessions в localStorage лежит уже session_token,
// а не мастер ADMIN_TOKEN. Старые ключи (до миграции) автоматически отвалятся
// при первом /verify — браузер получит 401 и попросит залогиниться заново.
const TOKEN_KEY = 'nkey_admin_token';

export const AuthProvider = ({ children }) => {
  const [token, setToken]                     = useState(localStorage.getItem(TOKEN_KEY) || '');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked]         = useState(false);
  const [sessionInfo, setSessionInfo]         = useState(null);

  const doVerify = useCallback(async (t) => {
    if (!t) return null;
    try {
      const res = await fetch(`${RELAY_URL}/api/dashboard/verify`, {
        headers: { 'X-Admin-Token': t, 'Content-Type': 'application/json' }
      });
      if (!res.ok) return null;
      const data = await res.json().catch(() => null);
      return data?.session || { id: null, label: null };
    } catch {
      return null;
    }
  }, []);

  // Проверка сохранённого session_token на старте.
  useEffect(() => {
    if (!token) {
      setAuthChecked(true);
      return;
    }
    doVerify(token).then(session => {
      if (session) {
        setSessionInfo(session);
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem(TOKEN_KEY);
        setToken('');
      }
      setAuthChecked(true);
    });
  }, []); // eslint-disable-line

  // Логин: либо мастер-токен (полный админ), либо логин+пароль профиля.
  // Принимает строку (мастер-токен, старый вызов) или объект:
  //   { adminToken } | { username, password }
  // Секреты в браузере не сохраняем — только session_token.
  const login = async (credentials) => {
    const body = typeof credentials === 'string' || credentials.adminToken !== undefined
      ? { admin_token: typeof credentials === 'string' ? credentials : credentials.adminToken }
      : { username: credentials.username, password: credentials.password };

    const res = await fetch(`${RELAY_URL}/api/dashboard/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data?.success || !data.session_token) {
      throw new Error(data?.error || 'Login failed');
    }

    localStorage.setItem(TOKEN_KEY, data.session_token);
    setToken(data.session_token);
    setSessionInfo({ id: null, label: data.label, role: data.role || 'admin', profile: data.profile || null });
    setIsAuthenticated(true);
  };

  const logout = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (t) {
      // best-effort: если revoke не прошёл (нет сети), всё равно вычищаем
      // токен из браузера — пользователь увидит логин-форму.
      try {
        await fetch(`${RELAY_URL}/api/dashboard/logout`, {
          method: 'POST',
          headers: { 'X-Admin-Token': t, 'Content-Type': 'application/json' },
        });
      } catch {}
    }
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setSessionInfo(null);
    setIsAuthenticated(false);
  }, []);

  const fetchAuthorized = useCallback(async (url, options = {}) => {
    const fullUrl = url.startsWith('http') ? url : `${RELAY_URL}${url}`;
    const headers = {
      ...options.headers,
      'X-Admin-Token': token,
      'Content-Type': 'application/json',
    };
    const response = await fetch(fullUrl, { ...options, headers });
    if (response.status === 401) {
      // Сессия revoked / истекла / не существует — выкидываем в логин.
      // logout() здесь нельзя await'ить (сама logout вызовет 401 → рекурсия).
      localStorage.removeItem(TOKEN_KEY);
      setToken('');
      setSessionInfo(null);
      setIsAuthenticated(false);
      throw new Error('Unauthorized');
    }
    return response;
  }, [token]);

  // Роль и права текущей сессии. Полный админ может всё; профиль — только то,
  // что включено администратором в его permissions.
  const role        = sessionInfo?.role || 'admin';
  const isAdmin     = role === 'admin';
  const permissions = sessionInfo?.profile?.permissions || null;
  const can = useCallback(
    (permKey) => (sessionInfo?.role || 'admin') === 'admin' || !!sessionInfo?.profile?.permissions?.[permKey],
    [sessionInfo]
  );

  return (
    <AuthContext.Provider value={{
      token, isAuthenticated, authChecked, sessionInfo,
      role, isAdmin, permissions, can,
      login, logout, fetchAuthorized,
      relayUrl: RELAY_URL,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
